import { useCallback, useEffect, useId, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { decryptIdFromUrl, encryptIdForUrl } from "@/utils/urlCrypto";
import {
  ArrowLeft,
  Bell,
  Calendar,
  CheckCircle2,
  FileImage,
  Loader2,
  MapPin,
  MessageSquare,
  Tag,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { NotFoundState } from "@/components/ui/empty-state";
import { SolicitacaoStatusBadge } from "@/components/solicitacoes/SolicitacaoStatusBadge";
import type { StatusSolicitacao } from "@/types/solicitacao";
import { cn } from "@/lib/utils";
import { resolveMediaUrl } from "@/lib/media";
import { toast } from "@/hooks/use-toast";
import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";
import { notifHeaders } from "@/lib/notificacoesAuth";

// Normaliza o `status` que vem da API ("Pendente", "Em análise", "Agendada",
// "Resolvida", ...) para a chave interna esperada por STATUS_LABELS / STATUS_COLORS
// (mesma lógica do solicitacoesStore.mapApiToParcial).
const normalizeApiStatus = (raw?: string | null): StatusSolicitacao | null => {
  if (!raw) return null;
  const key = raw
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, " ")
    .trim();
  const map: Record<string, StatusSolicitacao> = {
    pendente: "pendente",
    "aguardando aprovacao": "pendente",
    aguardando: "pendente",
    "em analise": "EmAnalise",
    emanalise: "EmAnalise",
    "em atendimento": "EmAnalise",
    agendada: "agendada",
    concluida: "resolvida",
    resolvida: "resolvida",
    aprovada: "aprovada",
    reprovada: "reprovada",
    arquivada: "arquivada",
  };
  return map[key] || null;
};

// Badge de Estado de Aprovação. Mantém a mesma paleta usada em
// `SolicitacaoStatusBadge`:
//  - Aguardando Aprovação → vermelho (mesma do Pendente)
//  - Reprovada → preto/branco invertido conforme tema
//  - Aprovada → verde
const normalizeEstado = (v?: string | null) =>
  (v || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s_-]+/g, "")
    .toLowerCase();

const EstadoAprovacaoBadge = ({ estado }: { estado?: string | null }) => {
  if (!estado) return null;
  const norm = normalizeEstado(estado);
  const base =
    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";

  if (norm === "aguardandoaprovacao") {
    return (
      <span
        className={cn(
          base,
          "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
        )}
      >
        Aguardando Aprovação
      </span>
    );
  }
  if (norm === "reprovada") {
    return (
      <span className={cn(base, "bg-black text-white dark:bg-white dark:text-black")}>
        Reprovada
      </span>
    );
  }
  if (norm === "aprovada") {
    return (
      <span
        className={cn(
          base,
          "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
        )}
      >
        Aprovada
      </span>
    );
  }
  return <span className={cn(base, "bg-muted text-muted-foreground")}>{estado}</span>;
};

// Coração com gradiente igual ao usado em SolicitacaoDetalhesPage
const GradientFavoriteIcon = ({
  className = "h-5 w-5",
}: {
  className?: string;
}) => {
  const gradientId = useId();
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(var(--primary))" />
          <stop offset="100%" stopColor="hsl(var(--secondary))" />
        </linearGradient>
      </defs>
      <path fill="none" d="M0 0h24v24H0V0z" />
      <path
        fill={`url(#${gradientId})`}
        stroke={`url(#${gradientId})`}
        strokeWidth="1.25"
        strokeLinejoin="round"
        d="M20.2246094,4.5693359C17.9908447,2.3260498,14.4058838,2.1826172,12,4.2402344C9.5941162,2.1826172,6.0091553,2.3260498,3.7753906,4.5693359c-2.3632812,2.3798218-2.3632812,6.2207642,0,8.6005859l7.8701172,7.8955078c0.09375,0.0944824,0.2213745,0.147583,0.3544922,0.1474609c0.1331177,0.0001221,0.2607422-0.0529785,0.3544922-0.1474609l7.8701172-7.8955078C22.5878906,10.7901001,22.5878906,6.9491577,20.2246094,4.5693359z"
      />
    </svg>
  );
};

const api = new RemoteDataSource(Settings.urlBase);

interface Endereco {
  logradouro?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  pais?: string;
}

interface Anexo {
  id: string;
  arquivo: string;
}

interface TopicoMinimo {
  id: string;
  descricao: string;
}

interface SolicitacaoCompacta {
  id: string;
  topico?: TopicoMinimo;
  anexos?: Anexo[];
  endereco?: Endereco | null;
  upn?: string;
  estadoAprovacao?: string;
  criadaPorMim?: boolean;
  arquivada?: boolean;
  apoiando?: boolean;
  status?: string;
  dataCriacao?: string;
  totalApoiadores?: number;
  protocolo?: string;
}

interface NotificacaoDetalhe {
  id: string;
  titulo: string;
  corpo: string;
  categoria: string;
  lida: boolean;
  dataEnvio: string;
  solicitacao?: SolicitacaoCompacta | null;
}

const extractPayload = (raw: unknown): NotificacaoDetalhe | null => {
  if (!raw) return null;
  if (Array.isArray(raw) && raw[0]) return raw[0] as NotificacaoDetalhe;
  const r = raw as Record<string, unknown>;
  if (r.id) return r as unknown as NotificacaoDetalhe;
  if (r.data) {
    if (Array.isArray(r.data) && r.data[0])
      return r.data[0] as NotificacaoDetalhe;
    return r.data as NotificacaoDetalhe;
  }
  if (r.notificacao) return r.notificacao as NotificacaoDetalhe;
  if (r.item) return r.item as NotificacaoDetalhe;
  return r as unknown as NotificacaoDetalhe;
};

const categoriaColor = (categoria?: string) => {
  switch (categoria) {
    case "Alterações da ocorrência":
      return "bg-primary/10 text-primary border-primary/20";
    case "Atualização do app":
      return "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20";
    case "Novidades":
      return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20";
    case "Promoções/Anúncios":
      return "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
};

const formatarEndereco = (endereco?: Endereco | null) => {
  if (!endereco) return null;
  const parts = [
    endereco.logradouro,
    endereco.numero,
    endereco.bairro,
    endereco.cidade,
    endereco.estado,
    endereco.cep,
    endereco.pais,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
};

const formatarData = (iso?: string) => {
  if (!iso) return "—";
  try {
    return format(new Date(iso), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return iso;
  }
};

export default function NotificacaoDetalhesPage() {
  const { id: encryptedId } = useParams<{ id: string }>();
  const [decryptedId, setDecryptedId] = useState<string>("");
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [notificacao, setNotificacao] = useState<NotificacaoDetalhe | null>(
    null
  );
  const [erro, setErro] = useState<string | null>(null);

  // Decrypt ID from URL
  useEffect(() => {
    const decryptId = async () => {
      if (encryptedId) {
        const decrypted = await decryptIdFromUrl(encryptedId);
        setDecryptedId(decrypted);
      }
    };
    decryptId();
  }, [encryptedId]);

  const marcarComoLida = useCallback(async (notifId: string) => {
    const result = await api.post<unknown>({
      endpoint: `v1/notificacoes/${notifId}/ler`,
      headers: notifHeaders(),
    });
    if ("failure" in result) {
      // Error marking notification as read
    }
  }, []);

  const fetchNotificacao = useCallback(async () => {
    if (!decryptedId) return;
    setIsLoading(true);
    setErro(null);
    try {
      const result = await api.get<NotificacaoDetalhe>({
        endpoint: `v1/notificacoes/${decryptedId}`,
        headers: notifHeaders(),
        forceRefresh: true,
        serialize: (raw) => extractPayload(raw) as NotificacaoDetalhe,
      });

      if ("failure" in result) {
        setErro(result.failure.message || "Não foi possível carregar a notificação.");
        toast({
          title: "Erro ao carregar notificação",
          description: result.failure.message || "Tente novamente.",
          variant: "destructive",
        });
        return;
      }

      const data = result.success.data;
      if (!data) {
        setErro("Notificação não encontrada.");
        return;
      }

      setNotificacao(data);

      // Marca como lida ao visualizar, se ainda não estiver
      if (!data.lida) {
        marcarComoLida(data.id);
        setNotificacao((prev) => (prev ? { ...prev, lida: true } : prev));
      }
    } catch (error) {
      setErro(error instanceof Error ? error.message : "Erro desconhecido.");
    } finally {
      setIsLoading(false);
    }
  }, [decryptedId, marcarComoLida]);

  useEffect(() => {
    fetchNotificacao();
  }, [fetchNotificacao]);

  // Sempre que a página ganhar foco ou voltar à aba, recarrega os dados
  useEffect(() => {
    const handleFocus = () => {
      fetchNotificacao();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchNotificacao();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchNotificacao]);

  // Early return if no ID
  if (!decryptedId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">
          Carregando notificação...
        </span>
      </div>
    );
  }

  if (erro || !notificacao) {
    return (
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <NotFoundState
          resourceName="notificação"
          onGoBack={() => navigate(-1)}
        />
        {erro && (
          <p className="text-center text-sm text-muted-foreground mt-2">{erro}</p>
        )}
      </div>
    );
  }

  const solicitacao = notificacao.solicitacao;
  const enderecoStr = formatarEndereco(solicitacao?.endereco ?? undefined);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Voltar */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        className="-ml-2"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Voltar
      </Button>

      {/* Header da notificação */}
      <Card>
        <CardHeader>
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Bell className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge
                  variant="outline"
                  className={cn("font-medium", categoriaColor(notificacao.categoria))}
                >
                  {notificacao.categoria}
                </Badge>
                {notificacao.lida ? (
                  <Badge variant="outline" className="gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Lida
                  </Badge>
                ) : (
                  <Badge className="bg-primary text-primary-foreground">
                    Não lida
                  </Badge>
                )}
              </div>
              <CardTitle className="font-heading text-2xl leading-tight">
                {notificacao.titulo}
              </CardTitle>
              <CardDescription className="mt-2 flex items-center gap-2 text-xs">
                <Calendar className="h-3.5 w-3.5" />
                {formatarData(notificacao.dataEnvio)}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Separator className="mb-4" />
          <p className="text-sm leading-relaxed whitespace-pre-wrap">
            {notificacao.corpo}
          </p>
        </CardContent>
      </Card>

      {/* Solicitação atribuída */}
      {solicitacao && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="font-heading text-lg">
                  Solicitação vinculada
                </CardTitle>
                <CardDescription>
                  Detalhes da solicitação referente a esta notificação
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Tópico + status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {solicitacao.topico && (
                <div className="flex items-start gap-2">
                  <Tag className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Tópico</p>
                    <p className="text-sm font-medium">
                      {solicitacao.topico.descricao}
                    </p>
                  </div>
                </div>
              )}

              {(() => {
                const statusNorm = normalizeApiStatus(solicitacao.status);
                if (!statusNorm) return null;
                return (
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Status</p>
                      {/* Sem passar estadoAprovacao para que as cores do status apareçam sempre */}
                      <SolicitacaoStatusBadge status={statusNorm} />
                    </div>
                  </div>
                );
              })()}

              {solicitacao.estadoAprovacao && (
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Estado de aprovação
                    </p>
                    <EstadoAprovacaoBadge estado={solicitacao.estadoAprovacao} />
                  </div>
                </div>
              )}

              {solicitacao.dataCriacao && (
                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Criada em</p>
                    <p className="text-sm">
                      {formatarData(solicitacao.dataCriacao)}
                    </p>
                  </div>
                </div>
              )}

              {solicitacao.upn && (
                <div className="flex items-start gap-2">
                  <Users className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Solicitante</p>
                    <p className="text-sm">{solicitacao.upn}</p>
                  </div>
                </div>
              )}

              {typeof solicitacao.totalApoiadores === "number" && (
                <div className="flex items-start gap-2">
                  <GradientFavoriteIcon className="h-5 w-5 mt-0.5 flex-shrink-0 drop-shadow-sm" />
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Total de apoiadores
                    </p>
                    <p className="text-sm font-medium">
                      {solicitacao.totalApoiadores}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Endereço */}
            {enderecoStr && (
              <>
                <Separator />
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Endereço</p>
                    <p className="text-sm">{enderecoStr}</p>
                  </div>
                </div>
              </>
            )}

            {/* Anexos */}
            {solicitacao.anexos && solicitacao.anexos.length > 0 && (
              <>
                <Separator />
                <div>
                  <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
                    <FileImage className="h-3.5 w-3.5" />
                    Anexos ({solicitacao.anexos.length})
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {solicitacao.anexos.map((anexo) => (
                      <a
                        key={anexo.id}
                        href={resolveMediaUrl(anexo.arquivo) || anexo.arquivo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block aspect-square rounded-lg overflow-hidden border border-border hover:border-primary transition-colors bg-muted"
                      >
                        <img
                          src={resolveMediaUrl(anexo.arquivo) || anexo.arquivo}
                          alt="Anexo"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      </a>
                    ))}
                  </div>
                </div>
              </>
            )}

            <Separator />
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (solicitacao.id) {
                    const encryptedId = await encryptIdForUrl(solicitacao.id);
                    navigate(`/solicitacoes/${encryptedId}`);
                  }
                }}
              >
                Abrir solicitação completa
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
